import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { useFitCheckBasket } from "@/app/context/FitCheckBasket";
import { JobDetails as FitCheckJobDetails } from "@/app/Views/FitCheck/FitCheck";

import { JobDetails } from "../utils/helpers";

interface UseFitCheckFlowReturn {
	showFitCheckMe: boolean;
	setShowFitCheckMe: React.Dispatch<React.SetStateAction<boolean>>;
	handleFitCheckMe: () => void;
	isOnProfilePage: boolean;
	isInBasket: (jobId: string) => boolean;
}

/**
 * Hook to manage FitCheck flow and basket interactions
 * @param jobDetails - The current job details
 * @param setShowJobDetailsModal - Optional callback to close parent modal
 * @returns FitCheck state and handlers
 */
export function useFitCheckFlow(
	jobDetails: JobDetails,
	setShowJobDetailsModal?: (show: boolean) => void,
): UseFitCheckFlowReturn {
	const [showFitCheckMe, setShowFitCheckMe] = useState(false);
	const location = useLocation();
	const { addToBasket, isInBasket } = useFitCheckBasket();
	const isOnProfilePage = location.pathname === "/profile";

	const handleFitCheckMe = () => {
		if (isOnProfilePage && jobDetails?._id) {
			// On profile page, add to basket instead of showing FitCheck flow
			if (isInBasket(jobDetails._id)) {
				toast.info("This job is already in your FitCheck basket");
				return;
			}
			addToBasket(jobDetails as unknown as FitCheckJobDetails);
			toast.success("Job added to FitCheck basket!");
			// Close the job details modal after adding to basket
			if (setShowJobDetailsModal) {
				setShowJobDetailsModal(false);
			}
		} else {
			// On other pages, show the FitCheck flow
			setShowFitCheckMe(true);
		}
	};

	return {
		showFitCheckMe,
		setShowFitCheckMe,
		handleFitCheckMe,
		isOnProfilePage,
		isInBasket,
	};
}
